// Header dropdown showing current streak, badge count, recent unlocks, and next trophy progress.
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Trophy, Flame, ChevronRight, Sparkles } from 'lucide-react';
import api from './studentApi';

export default function BadgesDropdown({ refreshTick = 0, compact = false }) {
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const ref = useRef();

  const load = async () => {
    try {
      const d = await api.get('/student/badges/summary');
      setData(d);
    } catch { /* silent */ }
  };

  useEffect(() => { load(); }, [refreshTick]);

  // Close on outside-click
  useEffect(() => {
    if (!open) return;
    const onDown = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  const summary = data?.summary || {};
  const recent = data?.recent_badges || [];
  const next = data?.next_trophy;
  const trophyPct = next ? Math.min(100, Math.round((next.progress / next.threshold) * 100)) : 0;

  return (
    <div className="relative" ref={ref}>
      <button onClick={() => setOpen(o => !o)} data-testid="badges-dropdown-btn"
        className="flex items-center gap-1.5 px-3 py-1.5 rounded-full border-2 text-xs font-semibold transition-all hover:-translate-y-0.5"
        style={{ borderColor: 'var(--st-border)', background: 'var(--st-card)' }}
        title="Badges & trophies">
        <Trophy className="w-3.5 h-3.5" style={{ color: 'var(--st-accent)' }} />
        <span data-testid="badges-count">{summary.badges_earned_count || 0}</span>
        {!compact && summary.current_streak > 0 && (
          <>
            <span className="opacity-50">·</span>
            <span className="flex items-center gap-0.5"><Flame className="w-3 h-3 text-orange-500" />{summary.current_streak}</span>
          </>
        )}
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-2 z-50 w-80 sm:w-96 rounded-2xl shadow-2xl border-2 overflow-hidden"
          style={{ background: 'var(--st-card)', borderColor: 'var(--st-border)' }}
          data-testid="badges-dropdown-panel">
          {/* Hero strip */}
          <div className="p-5 relative overflow-hidden"
            style={{ background: 'linear-gradient(135deg, var(--st-primary) 0%, var(--st-accent) 100%)', color: '#fff' }}>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-[10px] font-bold uppercase tracking-widest opacity-80">My Achievements</p>
                <div className="flex items-baseline gap-2 mt-1">
                  <span className="text-4xl font-extrabold leading-none">{summary.badges_earned_count || 0}</span>
                  <span className="text-xs opacity-80">badges earned</span>
                </div>
              </div>
              <Trophy className="w-12 h-12 opacity-30" />
            </div>
            <div className="grid grid-cols-3 gap-2 mt-4">
              <Stat icon={<Flame className="w-3 h-3" />} label="Streak" value={`${summary.current_streak || 0}d`} />
              <Stat label="Miles" value={summary.total_miles || 0} />
              <Stat label="Today" value={`${Math.round(summary.minutes_today || 0)}m`} />
            </div>
          </div>

          {/* Next trophy progress */}
          {next && (
            <div className="p-4 border-b" style={{ borderColor: 'var(--st-border)' }}>
              <div className="flex items-center gap-3">
                <div className="text-3xl">{next.icon}</div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-bold truncate">Next: {next.name}</div>
                  <div className="text-[11px] opacity-60">{next.remaining} more badges to unlock</div>
                </div>
              </div>
              <div className="mt-3 h-2 rounded-full overflow-hidden" style={{ background: 'var(--st-border)' }}>
                <div className="h-full rounded-full transition-all"
                  style={{ width: `${trophyPct}%`, background: next.color }}
                  data-testid="next-trophy-progress" />
              </div>
              <div className="flex justify-between text-[10px] opacity-60 mt-1">
                <span>{next.progress}</span>
                <span>{next.threshold}</span>
              </div>
            </div>
          )}

          {/* Recent badges */}
          <div className="p-3">
            <div className="text-[10px] font-bold uppercase tracking-widest opacity-60 px-2 mb-2">Recent unlocks</div>
            {recent.length === 0 ? (
              <div className="px-3 py-6 text-center text-xs opacity-60">
                <Sparkles className="w-5 h-5 mx-auto opacity-50 mb-2" />
                Stay active and ask questions to start earning badges!
              </div>
            ) : (
              <div className="space-y-1">
                {recent.map(b => (
                  <div key={b.id} className="flex items-center gap-3 p-2 rounded-xl hover:bg-black/5">
                    <div className="text-2xl flex-shrink-0">{b.icon}</div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-bold truncate">{b.name}</div>
                      <div className="text-[10px] opacity-60 truncate">{b.desc} · {timeAgo(b.earned_at)}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <button onClick={() => { setOpen(false); nav('/student/badges'); }}
            data-testid="view-all-badges-link"
            className="w-full p-3 text-xs font-bold flex items-center justify-center gap-1 border-t hover:bg-black/5"
            style={{ borderColor: 'var(--st-border)', color: 'var(--st-primary)' }}>
            View all badges & trophies <ChevronRight className="w-3.5 h-3.5" />
          </button>
        </div>
      )}
    </div>
  );
}

function Stat({ icon, label, value }) {
  return (
    <div className="rounded-lg bg-white/15 px-2 py-1.5">
      <div className="text-[9px] uppercase tracking-wider opacity-80 flex items-center gap-1">{icon}{label}</div>
      <div className="text-sm font-bold mt-0.5">{value}</div>
    </div>
  );
}

function timeAgo(iso) {
  if (!iso) return '';
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
  return new Date(iso).toLocaleDateString('en', { day: 'numeric', month: 'short' });
}
